import { Component, type ErrorInfo, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, RefreshCw, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BoundaryProps {
  children: ReactNode;
  /**
   * When this key changes (e.g. the device id in the URL), the boundary
   * automatically clears any prior error so navigating to a different
   * device recovers without a manual reload.
   */
  resetKey?: string;
  onBack?: () => void;
}

interface BoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  copied: boolean;
}

const INITIAL_STATE: BoundaryState = {
  hasError: false,
  error: null,
  componentStack: null,
  copied: false,
};

/**
 * Route-level error boundary for the Device Detail page.
 *
 * Renders a safe, scoped fallback (instead of the global crash screen) so:
 *   - the app shell / sidebar / navigation stays usable
 *   - the user can go back to the devices list, retry, or copy diagnostics
 *   - the boundary auto-resets when the device id changes
 *
 * This is intentionally defensive: any thrown error inside the route is
 * caught and surfaced as readable text rather than blanking the page.
 */
class DeviceDetailRouteBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = INITIAL_STATE;

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the failure in the console for triage while keeping UI safe.
    // eslint-disable-next-line no-console
    console.error("DeviceDetail route crashed:", error, info);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  componentDidUpdate(prevProps: BoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  private reset = () => this.setState(INITIAL_STATE);

  private handleRetry = () => {
    this.reset();
  };

  private handleCopy = async () => {
    const { error, componentStack } = this.state;
    const payload = [
      `Message: ${error?.message ?? "Unknown error"}`,
      `URL: ${typeof window !== "undefined" ? window.location.href : ""}`,
      `Time: ${new Date().toISOString()}`,
      `User-Agent: ${typeof navigator !== "undefined" ? navigator.userAgent : ""}`,
      "",
      "Stack:",
      error?.stack ?? "(no stack)",
      "",
      "Component stack:",
      componentStack ?? "(no component stack)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(payload);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      if (typeof window !== "undefined") {
        window.prompt("Copy device error details:", payload);
      }
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, copied } = this.state;
    const message = error?.message?.trim() || "An unexpected error occurred while loading this device.";

    return (
      <div className="px-6 py-6 space-y-6">
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 max-w-3xl">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-destructive/10 p-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div className="flex-1 space-y-2">
              <h2 className="text-lg font-semibold text-foreground">
                We couldn't display this device
              </h2>
              <p className="text-sm text-muted-foreground">
                Something in the device data caused this page to fail. The rest of
                the console is unaffected — you can go back to the devices list,
                retry, or share the details below with support.
              </p>
              <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs text-muted-foreground">
                {message}
              </pre>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {this.props.onBack && (
              <Button variant="default" onClick={this.props.onBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to devices
              </Button>
            )}
            <Button variant="secondary" onClick={this.handleRetry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try again
            </Button>
            <Button variant="outline" onClick={this.handleCopy}>
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy details
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Hook-friendly wrapper that wires the boundary to the current route id and
 * provides a "Back to devices" handler via React Router.
 */
export function DeviceDetailRouteBoundaryWithRouter({ children }: { children: ReactNode }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <DeviceDetailRouteBoundary resetKey={id} onBack={() => navigate("/devices")}>
      {children}
    </DeviceDetailRouteBoundary>
  );
}

export default DeviceDetailRouteBoundary;
